// code-guardian-ignore-duplicates: legacy self-contained Targets page (embedded in Employee Score); keeps its own month-input helpers rather than depending on the Sales & Targets module
import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Target as TargetIcon, Settings2, Plus, Upload, Filter as FilterIcon, Loader2, AlertCircle, Check, Search, Award } from 'lucide-react';
import Modal from '../components/Modal';
import { BlockLoader, InlineLoader } from '../components/loading';
import ManageMetricsModal, { TargetMetric } from '../components/ManageMetricsModal';
import TargetBulkUploadModal from '../components/TargetBulkUploadModal';
import TargetFilterApplyModal from '../components/TargetFilterApplyModal';
import { targetsAPI, targetMetricsAPI, storesAPI, employeesAPI, appleScorecardAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';

const APPLE_PROJECT_ID = 170;

type Scope = 'store' | 'employee';
type TabKey = 'set' | 'achievements' | 'target_score' | 'target_slabs';
type Field = 'target_value' | 'achieved_value' | 'achieved_pct';

interface TargetAchievementRow {
  employee_code: string;
  employee_name: string;
  store_code: string;
  store_name: string;
  store_category: string;
  target_value: number | null;
  achieved_value: number | null;
  achieved_pct: number | null;
  points: number;
}

interface SlabRow {
  parameter: string;
  lower_bound: number | null;
  upper_bound: number | null;
  points: number;
  display_label: string;
  display_order: number;
}

interface TargetRow {
  id: string | null;
  target_id?: string | null;
  project_id: number;
  metric_id: number;
  metric_name?: string;
  unit?: string | null;
  higher_is_better?: boolean;
  scope_type: Scope;
  store_id: string | null;
  employee_code: string | null;
  period_month: string;
  target_value: string | number | null;
  achieved_value: string | number | null;
  achieved_pct: string | number | null;
  notes: string | null;
  store_code?: string;
  store_name?: string;
  city?: string;
  state?: string;
  employee_name?: string;
  updated_by?: string;
  updated_at?: string;
}

interface StoreLite {
  id: string;
  store_code?: string;
  store_name?: string;
}

interface EmployeeLite {
  employee_code: string;
  employee_name?: string;
  isDeployed?: boolean;
}

const BASE_TABS: { key: TabKey; label: string; description: string }[] = [
  { key: 'set', label: 'Set Targets', description: 'Define monthly targets for stores or employees' },
  { key: 'achievements', label: 'Upload Achievements', description: 'Record what was actually achieved — pick any past month from the Period selector' },
];

const APPLE_TABS: { key: TabKey; label: string; description: string }[] = [
  { key: 'target_score', label: 'Target Achievement Score', description: 'Commercial score (0–100) based on monthly target achievement' },
  { key: 'target_slabs', label: 'Slab Structure', description: 'Points awarded for each Target Achieved % band' },
];

const monthInputToPeriod = (value: string): string => `${value}-01`;
const periodToMonthInput = (period: string): string => (period ? period.slice(0, 7) : '');
const todayMonthInput = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const lastMonthInput = (): string => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const fmtNum = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!isFinite(n)) return '—';
  return new Intl.NumberFormat('en-IN').format(n);
};

const computePct = (target: any, achieved: any): string => {
  if (target === null || achieved === null || target === undefined || achieved === undefined) return '—';
  const t = Number(target);
  const a = Number(achieved);
  if (!isFinite(t) || !isFinite(a) || t === 0) return '—';
  return `${((a / t) * 100).toFixed(1)}%`;
};

// User-supplied achieved_pct wins; otherwise derive from values.
const displayPct = (row: { target_value: any; achieved_value: any; achieved_pct: any }): string => {
  if (row.achieved_pct !== null && row.achieved_pct !== undefined && row.achieved_pct !== '') {
    const p = Number(row.achieved_pct);
    if (isFinite(p)) return `${p.toFixed(1)}%`;
  }
  return computePct(row.target_value, row.achieved_value);
};

// Props for inline rendering inside the unified Employee Score page (see
// Manage → Targets & Achievements sub-tab). Standalone routing leaves them
// undefined and behaviour is unchanged.
interface TargetsPageProps {
  /** Hide the page-level header (title + description) so the parent shell
   *  can provide its own chrome. The period picker + manage-metrics button
   *  stay visible (re-aligned to the right). */
  embedded?: boolean;
  /** When set: locks the metric selector to this target_metrics.id and
   *  suppresses the dropdown. Used by per-metric Manage sub-tabs in the
   *  Employee Score shell so each included target metric gets its own
   *  data-entry surface. */
  lockedMetricId?: number;
  /** When set: pins the By Store / By Employee scope toggle to this value
   *  and hides the toggle. Used when the parent already chose attribution
   *  scope on Configure — admins shouldn't need to pick again here. */
  lockedScope?: 'store' | 'employee';
}

export default function TargetsPage({ embedded, lockedMetricId, lockedScope }: TargetsPageProps = {}) {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const isAppleProject = selectedProjectId === APPLE_PROJECT_ID;

  // Tabs: base 2 for every project + 2 extra (Target Achievement Score + Slab
  // Structure) when Apple (170) is selected. The new tabs use the same
  // is_scorecard_metric data the Behavioral Scorecard uses, but with a heavier
  // slab so the commercial conversation stays distinct from the behavioral one.
  //
  // Inside the unified Employee Score shell (`embedded` mode) the Apple-only
  // tabs are suppressed — the commercial score and slab structure now live
  // on the unified Score and Configure tabs, so showing them here would
  // duplicate the surface.
  const TABS = useMemo(() => (
    isAppleProject && !embedded ? [...BASE_TABS, ...APPLE_TABS] : BASE_TABS
  ), [isAppleProject, embedded]);

  const [tab, setTab] = useState<TabKey>('set');
  const [scope, setScope] = useState<Scope>(lockedScope ?? 'store');
  // Mirror lockedScope into local state when the parent updates the prop
  // (e.g. admin flips the Configure-tab Employee/Store toggle while we're
  // mounted). Treats `undefined` as "no lock" and leaves scope alone.
  useEffect(() => {
    if (lockedScope !== undefined && lockedScope !== scope) {
      setScope(lockedScope);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedScope]);
  const [periodInput, setPeriodInput] = useState(todayMonthInput());

  // Target Achievement Score (Apple-only sub-tab) — separate fetch from the
  // existing spreadsheet so loading one doesn't disrupt the other.
  const [achievementRows, setAchievementRows] = useState<TargetAchievementRow[]>([]);
  const [loadingAchievement, setLoadingAchievement] = useState(false);
  const [achievementError, setAchievementError] = useState<string | null>(null);
  const [achievementSearch, setAchievementSearch] = useState('');

  // Slab structure for the Target Achievement Score (DB-driven via apple_scorecard_slabs).
  const [targetSlabs, setTargetSlabs] = useState<SlabRow[]>([]);
  const [loadingTargetSlabs, setLoadingTargetSlabs] = useState(false);
  const [metrics, setMetrics] = useState<TargetMetric[]>([]);
  // When the parent locks us to a specific metric (per-metric Manage sub-tab
  // in the Employee Score shell), we mirror the prop into our state below
  // via a useEffect so other code paths that read selectedMetricId stay
  // happy without sprinkling the prop everywhere.
  const [selectedMetricId, setSelectedMetricId] = useState<number | null>(lockedMetricId ?? null);
  useEffect(() => {
    if (lockedMetricId !== undefined && lockedMetricId !== selectedMetricId) {
      setSelectedMetricId(lockedMetricId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedMetricId]);
  const [rows, setRows] = useState<TargetRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showMetricsDrawer, setShowMetricsDrawer] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showBulkFilter, setShowBulkFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [stores, setStores] = useState<StoreLite[]>([]);
  const [deployedEmployees, setDeployedEmployees] = useState<EmployeeLite[]>([]);
  const [undeployedEmployees, setUndeployedEmployees] = useState<EmployeeLite[]>([]);

  // Single ordered list of every project employee (deployed first, then undeployed)
  // used both by the spreadsheet renderer and the Add Target modal picker.
  const allProjectEmployees = useMemo<EmployeeLite[]>(
    () => [...deployedEmployees, ...undeployedEmployees],
    [deployedEmployees, undeployedEmployees]
  );

  // Period defaults follow the tab semantics
  useEffect(() => {
    if (tab === 'achievements') setPeriodInput(lastMonthInput());
    else if (tab === 'set') setPeriodInput(todayMonthInput());
  }, [tab]);

  // If the project changes and we land on an Apple-only tab while no longer on
  // Apple, fall back to Set Targets so the UI never shows broken content.
  useEffect(() => {
    if (!isAppleProject && (tab === 'target_score' || tab === 'target_slabs')) {
      setTab('set');
    }
  }, [isAppleProject, tab]);

  // ── Target Achievement Score loaders ───────────────────────────────────
  const loadAchievementScore = useCallback(async () => {
    if (!isAppleProject) return;
    setAchievementError(null);
    setLoadingAchievement(true);
    try {
      const res = await appleScorecardAPI.getTargetAchievementScore(APPLE_PROJECT_ID, monthInputToPeriod(periodInput));
      setAchievementRows(res.data?.data || []);
    } catch (err: any) {
      setAchievementError(err?.response?.data?.message || err?.message || 'Failed to load Target Achievement Score');
    } finally {
      setLoadingAchievement(false);
    }
  }, [isAppleProject, periodInput]);

  const loadTargetSlabs = useCallback(async () => {
    if (!isAppleProject) return;
    setLoadingTargetSlabs(true);
    try {
      const res = await appleScorecardAPI.getSlabs(APPLE_PROJECT_ID);
      const all: Record<string, SlabRow[]> = res.data?.data || {};
      setTargetSlabs(all.target_achievement_pct || []);
    } catch {
      // non-fatal
    } finally {
      setLoadingTargetSlabs(false);
    }
  }, [isAppleProject]);

  useEffect(() => {
    if (tab === 'target_score') loadAchievementScore();
    else if (tab === 'target_slabs') loadTargetSlabs();
  }, [tab, loadAchievementScore, loadTargetSlabs]);

  // Client-side filter for the achievement score table
  const filteredAchievementRows = useMemo(() => {
    const needle = achievementSearch.trim().toLowerCase();
    if (!needle) return achievementRows;
    return achievementRows.filter((r) =>
      r.employee_code.toLowerCase().includes(needle) ||
      r.employee_name.toLowerCase().includes(needle) ||
      r.store_name.toLowerCase().includes(needle) ||
      r.store_code.toLowerCase().includes(needle)
    );
  }, [achievementRows, achievementSearch]);

  const fetchMetrics = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const res = await targetMetricsAPI.getAll(selectedProjectId);
      const list: TargetMetric[] = res.data?.data || [];
      setMetrics(list);
      setSelectedMetricId((prev) => {
        if (list.length === 0) return null;
        if (prev !== null && list.find((m) => m.id === prev)) return prev;
        return list[0].id;
      });
    } catch (err: any) {
      console.error('Failed to load metrics', err);
    }
  }, [selectedProjectId]);

  const fetchLookups = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const [sr, er] = await Promise.all([
        storesAPI.getAll(selectedProjectId),
        employeesAPI.getProjectEmployees(true, selectedProjectId),
      ]);
      setStores(sr.data?.data || sr.data || []);

      const normalise = (list: any[], deployed: boolean): EmployeeLite[] =>
        (list || [])
          .map((e: any) => ({
            employee_code: e.employee_code || e.emp_code || e.empCode || e.userName || '',
            employee_name: e.employee_name || e.emp_name || e.fullName || e.userName || '',
            isDeployed: deployed,
          }))
          .filter((e: EmployeeLite) => !!e.employee_code);

      const deployedList = er.data?.deployed || er.data?.data?.deployed || [];
      const undeployedList = er.data?.undeployed || er.data?.data?.undeployed || [];
      setDeployedEmployees(normalise(deployedList, true));
      setUndeployedEmployees(normalise(undeployedList, false));
    } catch (err) {
      console.error('Failed to load lookups', err);
    }
  }, [selectedProjectId]);

  const editingMode = tab === 'set' || tab === 'achievements';

  const fetchRows = useCallback(async () => {
    if (!selectedProjectId) return;
    // No metrics yet → nothing to fetch and the empty CTA takes over
    if (metrics.length === 0) {
      setRows([]);
      return;
    }
    // Editing tabs require a single metric to drive the spreadsheet
    if (editingMode && !selectedMetricId) {
      setRows([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await targetsAPI.list({
        projectId: selectedProjectId,
        periodMonth: monthInputToPeriod(periodInput),
        metricId: selectedMetricId || undefined,
        scope,
        includeSubjects: editingMode,
      });
      if (res.data?.status === 'success') {
        setRows(res.data.data || []);
      } else {
        setError(res.data?.message || 'Failed to load targets');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load targets');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, periodInput, selectedMetricId, scope, editingMode, metrics.length]);

  useEffect(() => {
    fetchMetrics();
    fetchLookups();
  }, [fetchMetrics, fetchLookups]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Apply an upsert response back into the row in-place so the user sees the
  // saved value without a full table reload.
  const applyRowUpdate = useCallback((row: TargetRow, field: Field, value: number | null) => {
    setRows((prev) =>
      prev.map((r) => {
        const sameStore = scope === 'store' && r.store_id === row.store_id;
        const sameEmp = scope === 'employee' && r.employee_code === row.employee_code;
        if (sameStore || sameEmp) {
          return { ...r, [field]: value };
        }
        return r;
      })
    );
  }, [scope]);

  const tabConfig = TABS.find((t) => t.key === tab)!;
  const activeMetric = metrics.find((m) => m.id === selectedMetricId) || null;

  const visibleRows = useMemo(() => {
    // Store scope: backend already returns one row per store (LEFT JOIN on stores).
    if (scope === 'store') return rows;

    // Employee scope: backend returns only the actual target rows. We merge them
    // here against the project's full employee roster (deployed + undeployed) so
    // every project employee appears even when no target row exists yet.
    if (!selectedMetricId || allProjectEmployees.length === 0) return rows;

    const byCode = new Map<string, TargetRow>();
    rows.forEach((r) => {
      if (r.employee_code) byCode.set(String(r.employee_code), r);
    });

    const periodIso = monthInputToPeriod(periodInput);
    const activeMetricInfo = metrics.find((m) => m.id === selectedMetricId);

    return allProjectEmployees
      .map((emp): TargetRow => {
        const existing = byCode.get(emp.employee_code);
        if (existing) return { ...existing, employee_name: existing.employee_name || emp.employee_name };
        return {
          id: null,
          target_id: null,
          project_id: selectedProjectId!,
          metric_id: selectedMetricId,
          metric_name: activeMetricInfo?.metric_name,
          unit: activeMetricInfo?.unit ?? null,
          higher_is_better: activeMetricInfo?.higher_is_better,
          scope_type: 'employee',
          store_id: null,
          employee_code: emp.employee_code,
          employee_name: emp.employee_name,
          period_month: periodIso,
          target_value: null,
          achieved_value: null,
          achieved_pct: null,
          notes: null,
        };
      })
      .sort((a, b) => String(a.employee_code).localeCompare(String(b.employee_code)));
  }, [rows, tab, scope, selectedMetricId, allProjectEmployees, periodInput, metrics, selectedProjectId]);

  // Apply client-side search on top of visibleRows.
  // Store scope: match against store_code / store_name. Employee scope: match
  // against employee_code / employee_name. Case-insensitive substring.
  const searchedRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visibleRows;
    return visibleRows.filter((r) => {
      if (r.scope_type === 'store') {
        return (
          String(r.store_code || '').toLowerCase().includes(q) ||
          String(r.store_name || '').toLowerCase().includes(q)
        );
      }
      return (
        String(r.employee_code || '').toLowerCase().includes(q) ||
        String(r.employee_name || '').toLowerCase().includes(q)
      );
    });
  }, [visibleRows, searchQuery]);

  const onDelete = async (row: TargetRow) => {
    if (!row.id) return;
    if (!window.confirm('Delete this target row?')) return;
    try {
      await targetsAPI.delete(row.id);
      fetchRows();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete');
    }
  };

  if (!selectedProjectId) {
    return (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
        Select a project to manage targets.
      </div>
    );
  }

  // ============================================================
  // Empty state — no metrics defined yet
  // ============================================================
  const noMetricsEmptyState = (
    <div className="rounded-xl border border-dashed border-primary-200 bg-primary-50/40 p-10 text-center">
      <TargetIcon className="mx-auto h-10 w-10 text-primary-500" />
      <h2 className="mt-3 text-lg font-semibold text-gray-900">Step 1 — Add a metric to start</h2>
      <p className="mt-1 text-sm text-gray-600">
        Targets are tracked against named metrics (e.g. Sales, Footfall, Audits Done). Define at least one
        metric for this project, then come back to set targets and upload achievements.
      </p>
      <button
        onClick={() => setShowMetricsDrawer(true)}
        className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
      >
        <Settings2 className="h-4 w-4" />
        Open Manage Metrics
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Outer page chrome — hidden when this page is embedded as a tab in the
          unified Employee Score shell. The period + Manage Metrics controls
          stay visible (right-aligned in their own row) since they belong to
          this tab's content, not the page header. */}
      {!embedded ? (
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
              <TargetIcon className="h-6 w-6 text-primary-600" />
              Targets & Achievements
            </h1>
            <p className="text-sm text-gray-500">{tabConfig.description}.</p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600">Period</label>
              <input
                type="month"
                value={periodInput}
                onChange={(e) => setPeriodInput(e.target.value)}
                className="mt-1 h-10 rounded-md border border-gray-300 px-3 text-sm focus:border-primary-500 focus:outline-none"
              />
            </div>
            <button
              onClick={() => setShowMetricsDrawer(true)}
              className="inline-flex h-10 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Settings2 className="h-4 w-4" />
              Manage Metrics
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-end justify-end gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-600">Period</label>
            <input
              type="month"
              value={periodInput}
              onChange={(e) => setPeriodInput(e.target.value)}
              className="mt-1 h-10 rounded-md border border-gray-300 px-3 text-sm focus:border-primary-500 focus:outline-none"
            />
          </div>
          <button
            onClick={() => setShowMetricsDrawer(true)}
            className="inline-flex h-10 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Settings2 className="h-4 w-4" />
            Manage Metrics
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${
                tab === t.key
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'target_score' ? (
        renderAchievementScoreTab({
          loading: loadingAchievement,
          error: achievementError,
          rows: filteredAchievementRows,
          totalRows: achievementRows.length,
          search: achievementSearch,
          setSearch: setAchievementSearch,
          onRefresh: loadAchievementScore,
          periodInput,
        })
      ) : tab === 'target_slabs' ? (
        renderTargetSlabsTab({ loading: loadingTargetSlabs, slabs: targetSlabs })
      ) : metrics.length === 0 ? (
        noMetricsEmptyState
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              {/* Scope toggle is hidden when the parent locks scope via
                  lockedScope — admins picked the attribution on Configure
                  when including the metric, so re-asking here would be
                  redundant (and would let the two surfaces disagree). */}
              {lockedScope === undefined && (
                <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
                  {(['store', 'employee'] as Scope[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => setScope(s)}
                      className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                        scope === s
                          ? 'bg-primary-600 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {s === 'store' ? 'By Store' : 'By Employee'}
                    </button>
                  ))}
                </div>
              )}
              {/* Metric selector is hidden when the parent locks us to one
                  metric via lockedMetricId — each per-metric Manage sub-tab
                  in Employee Score provides its own context. */}
              {lockedMetricId === undefined && (
                <div>
                  <select
                    value={selectedMetricId ?? ''}
                    onChange={(e) => setSelectedMetricId(e.target.value ? Number(e.target.value) : null)}
                    className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-primary-500 focus:outline-none"
                  >
                    {metrics.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.metric_name}
                        {m.unit ? ` (${m.unit})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={scope === 'store' ? 'Search store code or name' : 'Search employee code or name'}
                  className="h-10 w-64 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex h-10 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
                {tab === 'set' ? 'Add Target' : 'Add Achievement'}
              </button>
              <button
                onClick={() => setShowBulkUpload(true)}
                className="inline-flex h-10 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Upload className="h-4 w-4" />
                Bulk Upload
              </button>
              <button
                onClick={() => setShowBulkFilter(true)}
                disabled={!selectedMetricId}
                className="inline-flex h-10 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <FilterIcon className="h-4 w-4" />
                Apply by Filter
              </button>
            </div>
          </div>

          {/* Hint when in editing mode but no metric is selected */}
          {editingMode && !selectedMetricId && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Pick a metric above to start entering {tab === 'set' ? 'targets' : 'achievements'}.
            </div>
          )}

          {/* Tip for the Upload Achievements tab — clarifies that targets aren't a prerequisite */}
          {tab === 'achievements' && selectedMetricId && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <strong>Tip —</strong> you don't need to set targets first. Enter either a raw achieved value or
              the achievement % directly. Either is fine; the system will compute % from the values when both
              target and achieved are present.
            </div>
          )}

          {/* Table */}
          <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
                  <tr>
                    {scope === 'store' ? (
                      <>
                        <th className="px-4 py-3">Store Code</th>
                        <th className="px-4 py-3">Store Name</th>
                      </>
                    ) : (
                      <>
                        <th className="px-4 py-3">Employee Code</th>
                        <th className="px-4 py-3">Employee Name</th>
                      </>
                    )}
                    <th className="px-4 py-3 text-right">Target</th>
                    <th className="px-4 py-3 text-right">Achieved Value</th>
                    <th className="px-4 py-3 text-right">Achieved %</th>
                    <th className="px-4 py-3 text-right">%</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                        <BlockLoader className="py-0" />
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center">
                        <div className="inline-flex items-center gap-2 text-sm text-red-600">
                          <AlertCircle className="h-4 w-4" /> {error}
                        </div>
                      </td>
                    </tr>
                  ) : searchedRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">
                        {searchQuery
                          ? `No matches for "${searchQuery}".`
                          : scope === 'store'
                            ? 'No stores found in this project.'
                            : 'No deployed employees found in this project.'}
                      </td>
                    </tr>
                  ) : (
                    searchedRows.map((row, idx) => {
                      const emp = row.scope_type === 'employee'
                        ? allProjectEmployees.find((e) => e.employee_code === row.employee_code)
                        : null;
                      return (
                      <tr key={`${row.scope_type}-${row.store_id || row.employee_code || idx}`} className="hover:bg-gray-50">
                        {row.scope_type === 'store' ? (
                          <>
                            <td className="px-4 py-3 font-medium text-gray-900">{row.store_code || '—'}</td>
                            <td className="px-4 py-3 text-gray-900">
                              {row.store_name || '—'}
                              {row.city ? <span className="text-xs text-gray-500"> · {row.city}</span> : null}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium text-gray-900">{row.employee_code}</span>
                                {emp?.isDeployed === false && (
                                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                                    Undeployed
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-900">{row.employee_name || '—'}</td>
                          </>
                        )}

                        {/* Target column */}
                        <td className="px-4 py-3 text-right">
                          {tab === 'set' && selectedMetricId ? (
                            <EditableValueCell
                              row={row}
                              field="target_value"
                              projectId={selectedProjectId}
                              metricId={selectedMetricId}
                              periodMonth={monthInputToPeriod(periodInput)}
                              scope={scope}
                              onSaved={(v) => applyRowUpdate(row, 'target_value', v)}
                            />
                          ) : (
                            <span className="text-gray-900">{fmtNum(row.target_value)}</span>
                          )}
                        </td>

                        {/* Achieved Value column */}
                        <td className="px-4 py-3 text-right">
                          {tab === 'achievements' && selectedMetricId ? (
                            <EditableValueCell
                              row={row}
                              field="achieved_value"
                              projectId={selectedProjectId}
                              metricId={selectedMetricId}
                              periodMonth={monthInputToPeriod(periodInput)}
                              scope={scope}
                              onSaved={(v) => applyRowUpdate(row, 'achieved_value', v)}
                            />
                          ) : (
                            <span className="text-gray-900">{fmtNum(row.achieved_value)}</span>
                          )}
                        </td>

                        {/* Achieved % column — user-entered, overrides derived */}
                        <td className="px-4 py-3 text-right">
                          {tab === 'achievements' && selectedMetricId ? (
                            <EditableValueCell
                              row={row}
                              field="achieved_pct"
                              projectId={selectedProjectId}
                              metricId={selectedMetricId}
                              periodMonth={monthInputToPeriod(periodInput)}
                              scope={scope}
                              onSaved={(v) => applyRowUpdate(row, 'achieved_pct', v)}
                            />
                          ) : (
                            <span className="text-gray-900">{row.achieved_pct !== null && row.achieved_pct !== undefined && row.achieved_pct !== '' ? `${Number(row.achieved_pct).toFixed(1)}` : '—'}</span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-right text-gray-900">{displayPct(row)}</td>
                          <td className="px-4 py-3 text-center"><TableActionGroup>
                            {row.id ? (
                              <TableActionButton action="delete" label="Delete"
                                onClick={() => onDelete(row)}
                                title="Delete this target row" />
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </TableActionGroup></td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <ManageMetricsModal
        isOpen={showMetricsDrawer}
        onClose={() => setShowMetricsDrawer(false)}
        projectId={selectedProjectId}
        onMetricsChanged={fetchMetrics}
      />

      <AddTargetModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSaved={() => {
          setShowAddModal(false);
          fetchRows();
        }}
        projectId={selectedProjectId}
        scope={scope}
        metrics={metrics}
        defaultMetricId={selectedMetricId}
        defaultPeriod={monthInputToPeriod(periodInput)}
        stores={stores}
        employees={allProjectEmployees}
        mode={tab === 'achievements' ? 'achievement' : 'target'}
      />

      <TargetBulkUploadModal
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        onSuccess={fetchRows}
        projectId={selectedProjectId}
        scope={scope}
        title={tab === 'achievements' ? 'Bulk Upload Achievements' : 'Bulk Upload Targets'}
      />

      <TargetFilterApplyModal
        isOpen={showBulkFilter}
        onClose={() => setShowBulkFilter(false)}
        onSuccess={fetchRows}
        projectId={selectedProjectId}
        scope={scope}
        metricId={selectedMetricId}
        metricName={activeMetric?.metric_name || ''}
        periodMonth={monthInputToPeriod(periodInput)}
        mode={tab === 'achievements' ? 'achievement' : 'target'}
      />
    </div>
  );
}

// ============================================================
// EditableValueCell — saves on blur or Enter via upsertOne
// ============================================================
interface EditableProps {
  row: TargetRow;
  field: Field;
  projectId: number;
  metricId: number;
  periodMonth: string;
  scope: Scope;
  onSaved: (value: number | null) => void;
}

function EditableValueCell({ row, field, projectId, metricId, periodMonth, scope, onSaved }: EditableProps) {
  const initial = row[field];
  const initialStr = initial === null || initial === undefined || initial === '' ? '' : String(initial);
  const [value, setValue] = useState(initialStr);
  const [savedRecently, setSavedRecently] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // If a sibling save (different field) reloads the row, keep the input in sync.
  useEffect(() => {
    setValue(initialStr);
  }, [initialStr]);

  const commit = async () => {
    if (saving) return;
    if (value === initialStr) return; // nothing changed

    let numericValue: number | null = null;
    if (value !== '') {
      const n = Number(value);
      if (!isFinite(n)) {
        setErrorMsg('Invalid number');
        setValue(initialStr);
        return;
      }
      numericValue = n;
    }

    setSaving(true);
    setErrorMsg(null);
    try {
      await targetsAPI.upsertOne({
        project_id: projectId,
        metric_id: metricId,
        scope_type: scope,
        store_id: scope === 'store' ? row.store_id : null,
        employee_code: scope === 'employee' ? row.employee_code : null,
        period_month: periodMonth,
        [field]: numericValue,
      });
      onSaved(numericValue);
      setSavedRecently(true);
      window.setTimeout(() => setSavedRecently(false), 1200);
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || 'Save failed');
      setValue(initialStr);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inline-flex items-center justify-end gap-1.5">
      <input
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setValue(initialStr);
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder="—"
        className={`w-24 rounded border px-2 py-1 text-right text-sm focus:border-primary-500 focus:outline-none ${
          errorMsg ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
      />
      {saving ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
      ) : savedRecently ? (
        <Check className="h-3.5 w-3.5 text-green-500" />
      ) : errorMsg ? (
        <span title={errorMsg}>
          <AlertCircle className="h-3.5 w-3.5 text-red-500" />
        </span>
      ) : (
        <span className="h-3.5 w-3.5" />
      )}
    </div>
  );
}

// ============================================================
// Add Target / Add Achievement modal (single row upsert)
// ============================================================
interface AddTargetProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  projectId: number;
  scope: Scope;
  metrics: TargetMetric[];
  defaultMetricId: number | null;
  defaultPeriod: string;
  stores: StoreLite[];
  employees: EmployeeLite[];
  mode: 'target' | 'achievement';
}

function AddTargetModal({
  isOpen,
  onClose,
  onSaved,
  projectId,
  scope,
  metrics,
  defaultMetricId,
  defaultPeriod,
  stores,
  employees,
  mode,
}: AddTargetProps) {
  const [subject, setSubject] = useState('');
  const [metricId, setMetricId] = useState<number | null>(defaultMetricId);
  const [period, setPeriod] = useState(periodToMonthInput(defaultPeriod));
  const [targetValue, setTargetValue] = useState('');
  const [achievedValue, setAchievedValue] = useState('');
  const [achievedPct, setAchievedPct] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSubject('');
      setMetricId(defaultMetricId);
      setPeriod(periodToMonthInput(defaultPeriod));
      setTargetValue('');
      setAchievedValue('');
      setAchievedPct('');
      setNotes('');
      setError(null);
    }
  }, [isOpen, defaultMetricId, defaultPeriod]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!metricId) {
      setError('Pick a metric');
      return;
    }
    if (!subject) {
      setError(scope === 'store' ? 'Pick a store' : 'Pick an employee');
      return;
    }
    if (!targetValue && !achievedValue && !achievedPct) {
      setError('Enter at least one of Target Value, Achieved Value, or Achieved %');
      return;
    }
    setSubmitting(true);
    try {
      await targetsAPI.upsertOne({
        project_id: projectId,
        metric_id: metricId,
        scope_type: scope,
        store_id: scope === 'store' ? subject : null,
        employee_code: scope === 'employee' ? subject : null,
        period_month: monthInputToPeriod(period),
        target_value: targetValue ? Number(targetValue) : null,
        achieved_value: achievedValue ? Number(achievedValue) : null,
        achieved_pct: achievedPct ? Number(achievedPct) : null,
        notes: notes || null,
      });
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'achievement' ? 'Add Achievement' : 'Add Target'}
      size="lg"
      footer={
        <div className="flex w-full justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-target-form"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      }
    >
      <form id="add-target-form" onSubmit={submit} className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
            <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500 mt-0.5" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-gray-600">{scope === 'store' ? 'Store' : 'Employee'}</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              required
            >
              <option value="">Select {scope === 'store' ? 'store' : 'employee'}…</option>
              {scope === 'store'
                ? stores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.store_code} — {s.store_name}
                    </option>
                  ))
                : employees.map((e) => (
                    <option key={e.employee_code} value={e.employee_code}>
                      {e.employee_code} — {e.employee_name || '(no name)'}
                      {e.isDeployed === false ? ' [Undeployed]' : ''}
                    </option>
                  ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Metric</label>
            <select
              value={metricId ?? ''}
              onChange={(e) => setMetricId(e.target.value ? Number(e.target.value) : null)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              required
            >
              <option value="">Select metric…</option>
              {metrics.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.metric_name}
                  {m.unit ? ` (${m.unit})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Period (month)</label>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Target Value</label>
            <input
              type="number"
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              placeholder="—"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Achieved Value</label>
            <input
              type="number"
              value={achievedValue}
              onChange={(e) => setAchievedValue(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              placeholder="—"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Achieved %</label>
            <input
              type="number"
              value={achievedPct}
              onChange={(e) => setAchievedPct(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              placeholder="e.g. 92.5"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Fill in any combination — at least one is required. Achieved % overrides the derived value when present.
        </p>

        <div>
          <label className="text-xs font-medium text-gray-600">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Apple-only sub-tab renderers — kept here to keep TargetsPage.tsx self-contained.
// ─────────────────────────────────────────────────────────────────────────────

interface AchievementTabProps {
  loading: boolean;
  error: string | null;
  rows: TargetAchievementRow[];
  totalRows: number;
  search: string;
  setSearch: (v: string) => void;
  onRefresh: () => void;
  periodInput: string;
}

function renderAchievementScoreTab(p: AchievementTabProps) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={p.search}
              onChange={(e) => p.setSearch(e.target.value)}
              placeholder="Search employee code or name"
              className="h-10 w-72 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none"
            />
          </div>
          <p className="text-sm text-gray-500">
            {p.loading ? 'Calculating…' : `${p.rows.length} of ${p.totalRows} employees with targets in ${p.periodInput}.`}
          </p>
        </div>
        <button
          type="button"
          onClick={p.onRefresh}
          disabled={p.loading}
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          {p.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4 text-primary-600" />}
          Recalculate
        </button>
      </div>

      {p.error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{p.error}</span>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
            <tr>
              <th className="px-4 py-3">Emp Code</th>
              <th className="px-4 py-3">Emp Name</th>
              <th className="px-4 py-3">Store</th>
              <th className="px-4 py-3 text-right">Target</th>
              <th className="px-4 py-3 text-right">Achieved</th>
              <th className="px-4 py-3 text-right">Achieved %</th>
              <th className="px-4 py-3 text-right">Points (/120)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {p.rows.length === 0 && !p.loading && (
              <tr>
                <td className="px-4 py-8 text-center text-sm text-gray-500" colSpan={7}>
                  No achievement data for {p.periodInput}. Set targets and upload achievements first, then come back.
                </td>
              </tr>
            )}
            {p.rows.map((r) => (
              <tr key={r.employee_code} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-mono text-xs text-gray-700">{r.employee_code}</td>
                <td className="px-4 py-2 font-medium text-gray-900">{r.employee_name}</td>
                <td className="px-4 py-2 text-gray-700">
                  <div>{r.store_name || '—'}</div>
                  {(r.store_code || r.store_category) && (
                    <div className="text-xs text-gray-500">
                      {r.store_code}{r.store_code && r.store_category ? ' · ' : ''}{r.store_category}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtNum(r.target_value)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtNum(r.achieved_value)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{r.achieved_pct !== null ? `${r.achieved_pct.toFixed(1)}%` : '—'}</td>
                <td className="px-4 py-2 text-right text-base font-semibold tabular-nums text-primary-700">{r.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function renderTargetSlabsTab({ loading, slabs }: { loading: boolean; slabs: SlabRow[] }) {
  return (
    <div className="max-w-xl">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-1 font-semibold text-gray-900">Target Achieved % → Points</h3>
        <p className="mb-3 text-xs text-gray-500">
          The Target Achievement Score is a 0–100 commercial score based purely on iPU target attainment.
          Slabs are stored in <code className="rounded bg-gray-100 px-1">apple_scorecard_slabs</code>.
        </p>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <InlineLoader label="Loading slabs…" />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="py-1 text-left font-medium text-gray-600">Slab</th>
                <th className="py-1 text-right font-medium text-gray-600">Points</th>
              </tr>
            </thead>
            <tbody>
              {slabs.map((s) => (
                <tr key={s.display_order} className="border-b border-gray-100 last:border-0">
                  <td className="py-1.5 text-gray-700">{s.display_label}</td>
                  <td className="py-1.5 text-right font-medium tabular-nums">{s.points}</td>
                </tr>
              ))}
              {slabs.length === 0 && (
                <tr><td colSpan={2} className="py-4 text-center text-xs text-gray-500">No slabs configured yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
